import { useEffect, useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { useTenant } from "@/core/tenant/TenantProvider";
import { getDuel } from "@/duels/registry";
import { Alert, Button, Card, CardTitle, Switch, useToast } from "@/core/ui";
import {
  useNotificationPreference,
  useUpdateNotificationPreference,
  type NotificationChoice,
} from "./api";

/**
 * E-mail preferences, opt-in (ADR 0021). Every switch starts off; saving records the
 * user's answer even when everything stays off, so the first-login prompt stops asking.
 * The duel switch shows only where the tenant offers a duel (ADR 0076).
 */
export function NotificationsCard({ email }: { email: string }) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const tenant = useTenant();
  const preference = useNotificationPreference();
  const update = useUpdateNotificationPreference();
  const [form, setForm] = useState<NotificationChoice>({
    on_publication: false,
    weekly_digest: false,
    on_duel_turn: false,
  });
  useEffect(() => {
    if (preference.data) {
      setForm({
        on_publication: preference.data.on_publication,
        weekly_digest: preference.data.weekly_digest,
        on_duel_turn: preference.data.on_duel_turn,
      });
    }
  }, [preference.data]);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    update.mutate(form, {
      onSuccess: () => toast(t("admin.saved"), "success"),
      onError: () => toast(t("app.error_generic"), "danger"),
    });
  };

  return (
    <Card className="bt-account__notifications">
      <CardTitle>{t("account.notifications.title")}</CardTitle>
      <p className="bt-account__lead">{t("account.notifications.lead", { email })}</p>
      {tenant.notifications_enabled ? null : (
        <Alert tone="info">{t("account.notifications.tenant_off")}</Alert>
      )}
      <form onSubmit={submit}>
        <Switch
          id="notify-publication"
          checked={form.on_publication}
          onChange={(v) => setForm({ ...form, on_publication: v })}
          label={t("account.notifications.on_publication")}
        />
        <Switch
          id="notify-digest"
          checked={form.weekly_digest}
          onChange={(v) => setForm({ ...form, weekly_digest: v })}
          label={t("account.notifications.weekly_digest")}
        />
        {tenant.enabled_games.some((key) => getDuel(key)) ? (
          <Switch
            id="notify-duel-turn"
            checked={form.on_duel_turn}
            onChange={(v) => setForm({ ...form, on_duel_turn: v })}
            label={t("account.notifications.on_duel_turn")}
          />
        ) : null}
        <Button
          type="submit"
          className="bt-account__submit"
          loading={update.isPending}
          disabled={preference.isPending}
        >
          {t("admin.save")}
        </Button>
      </form>
    </Card>
  );
}
